import { useEffect } from 'react';
import { Check } from 'lucide-react';
import Header from '../components/site/Header';
import Hero from '../components/site/Hero';
import About from '../components/site/About';
import Services from '../components/site/Services';
import ExperienceSection from '../components/site/ExperienceSection';
import Process from '../components/site/Process';
import Gallery from '../components/site/Gallery';
import LocationsSection from '../components/site/LocationsSection';
import Testimonials from '../components/site/Testimonials';
import FAQ from '../components/site/FAQ';
import { BlogStrip } from '../components/site/BlogStrip';
import Contact from '../components/site/Contact';
import Footer from '../components/site/Footer';
import QuoteModal from '../components/site/QuoteModal';
import { useSEO } from '../hooks/useSEO';
import { useReveal } from '../hooks/useReveal';
import { useQuoteModal } from '../hooks/useQuoteModal';
import { services, additionalServices, business, testimonials, faqs } from '../mock';

export default function HomePage() {
  const [quoteOpen, setQuoteOpen] = useQuoteModal();
  const openQuote = () => setQuoteOpen(true);
  useReveal();

  // Handle #hash on first load or after a Navigate redirect (e.g. /contact/ → /#contact).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    // wait a tick for sections to mount before scrolling
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  useSEO({
    title: 'Vic Garden and Lawn Maintenance | Ballarat',
    description:
      'Lawn mowing, gardening and weed control for Ballarat homes, plus commercial grounds maintenance across Victoria. NDIS & DVA, fully insured. Free quotes: 0450 536 106.',
    canonical: 'https://vglm.com.au/',
    ogImage: 'https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/nXvGBSdAJRfNxUNGToP1/media/69ff3a657285562721eeab0b.png',
    keywords:
      'lawn mowing Ballarat, garden maintenance Ballarat, gardening near me, lawn mowing near me, weeding Ballarat, lawn care Ballarat, gardener Ballarat, acreage mowing Ballarat, ride on mowing Ballarat, lifestyle property mowing, commercial lawn mowing Ballarat, commercial grounds maintenance Ballarat, body corporate gardening Ballarat, real estate lawn care Ballarat',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': 'https://vglm.com.au/#webpage',
        url: 'https://vglm.com.au/',
        name: 'Vic Garden and Lawn Maintenance',
        isPartOf: { '@id': 'https://vglm.com.au/#website' },
        about: { '@id': 'https://vglm.com.au/#organization' },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/nXvGBSdAJRfNxUNGToP1/media/69ff3a657285562721eeab0b.png',
        },
        inLanguage: 'en-AU',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vglm.com.au/' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LawnService',
        '@id': 'https://vglm.com.au/#business',
        name: business.name,
        url: 'https://vglm.com.au/',
        telephone: '+61450536106',
        priceRange: '$$',
        areaServed: { '@type': 'City', name: 'Ballarat, VIC' },
        review: testimonials.slice(0, 5).map((t) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: t.name },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(t.rating || 5),
            bestRating: '5',
            worstRating: '1',
          },
          reviewBody: t.text,
          ...(t.datePublished ? { datePublished: t.datePublished } : {}),
        })),
      },
    ],
  });

  return (
    <>
      <Header onQuoteClick={openQuote} />
      <main>
        <Hero onQuoteClick={openQuote} />
        <About />
        <Services />
        <ExperienceSection />
        <Process />
        <Gallery />
        <LocationsSection onQuoteClick={openQuote} />

        <Testimonials variant="marquee" />
        <FAQ onQuoteClick={openQuote} />
        <BlogStrip />

        {/* All services strip — bottom-of-page summary */}
        <section className="py-16 cream-alt-bg border-t border-[#E2DAC5]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center">
            <p className="section-label mb-3">All Services</p>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              Everything we do for Ballarat properties

            </h2>
            <ul className="flex flex-wrap items-center justify-center gap-2.5 max-w-4xl mx-auto">
              {[...services.map((s) => s.title), ...additionalServices].map((label) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E2DAC5] text-[#0A2E18] text-sm font-semibold hover:border-[#1B6E37] hover:shadow-sm transition-all"
                >
                  <Check className="w-3.5 h-3.5 text-[#1B6E37]" aria-hidden="true" />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
